/**
 * File Upload Service
 */
import { getApiClient } from '../apiClient';
import { MEDIA_ENDPOINTS } from '../endpoints';
import type { UploadResponse, ApiResponse, MediaUploadResponse } from '../types';

import type { AxiosProgressEvent } from 'axios';

export interface UploadOptions {
  onProgress?: (percent: number, event: AxiosProgressEvent) => void;
  signal?: AbortSignal;
}

export const uploadService = {
  /**
   * Upload single file
   */
  uploadFile: async (
    formData: FormData,
    options?: UploadOptions
  ): Promise<ApiResponse<MediaUploadResponse>> => {
    const client = getApiClient().getInstance();

    const response = await client.post(MEDIA_ENDPOINTS.upload, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (options?.onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          options.onProgress(percent, progressEvent);
        }
      },
      signal: options?.signal,
    });

    // Extract data from axios response
    return response.data;
  },

  /**
   * Upload multiple files
   */
  uploadMultiple: async (
    formData: FormData,
    options?: UploadOptions
  ): Promise<ApiResponse<UploadResponse[]>> => {
    const client = getApiClient().getInstance();

    return client.post(MEDIA_ENDPOINTS.uploadMultiple, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (options?.onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          options.onProgress(percent, progressEvent);
        }
      },
      signal: options?.signal,
    });
  },

  /**
   * Delete file
   */
  deleteFile: async (id: string): Promise<ApiResponse<void>> => {
    return getApiClient().delete(MEDIA_ENDPOINTS.delete.replace(':id', id));
  },
};

